import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { isEqual } from 'lodash'
import { cn } from 'twyne-new.ui'

const PinnedContext = createContext({ pinnedIndexes: [], offsets: {}, headerRowRef: { current: null } })

const EMPTY_INDEXES = []

export function PinnedColumnsProvider({ pinnedIndexes = EMPTY_INDEXES, columns, children }) {
  const headerRowRef = useRef(null)
  const [offsets, setOffsets] = useState({})

  const pinnedCells = useCallback(() => {
    const cells = Array.from(headerRowRef.current?.querySelectorAll?.('th') ?? [])
    return pinnedIndexes.map(index => cells[index]).filter(Boolean)
  }, [pinnedIndexes])

  const measure = useCallback(() => {
    const widths = pinnedCells().map(cell => cell.getBoundingClientRect().width)
    const next = {}
    pinnedIndexes.forEach((index, position) => {
      next[index] = widths.slice(0, position).reduce((sum, width) => sum + width, 0)
    })
    setOffsets(current => isEqual(current, next) ? current : next)
  }, [pinnedCells, pinnedIndexes])

  useLayoutEffect(() => {
    measure()

    const cells = pinnedCells()
    if (!cells.length || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(measure)
    cells.forEach(cell => observer.observe(cell))
    return () => observer.disconnect()
  }, [measure, pinnedCells, columns])

  const value = useMemo(() => ({ pinnedIndexes, offsets, headerRowRef }), [pinnedIndexes, offsets])

  return <PinnedContext.Provider value={value}>
    {children}
  </PinnedContext.Provider>
}

export function usePinnedColumns() {
  return useContext(PinnedContext)
}

export const PINNED_DIVIDER_CLASS = 'shadow-[inset_-2px_0_0_0_var(--color-gray-300)] dark:shadow-[inset_-2px_0_0_0_var(--color-gray-1200)]'
export const PINNED_HEADER_CLASS = 'sticky top-0 z-30 twy-bg-base-accent before:absolute before:left-0 before:right-0 before:bottom-0 before:h-px before:bg-[var(--color-gray-300)] dark:before:bg-[var(--color-gray-1200)]'
export const PINNED_HEADER_DIVIDER_CLASS = 'after:absolute after:top-0 after:bottom-0 after:right-0 after:w-[2px] after:bg-[var(--color-gray-300)] dark:after:bg-[var(--color-gray-1200)]'

function pinnedHeaderClass(pinned, last) {
  if (!pinned) {
    return undefined
  }

  return cn(PINNED_HEADER_CLASS, last && PINNED_HEADER_DIVIDER_CLASS)
}

function isLastPinned(index, pinnedIndexes) {
  return pinnedIndexes[pinnedIndexes.length - 1] === index
}

export function usePinnedHeaderCell(index) {
  const { pinnedIndexes, offsets } = usePinnedColumns()
  const pinned = pinnedIndexes.includes(index)

  return {
    pinned,
    className: pinnedHeaderClass(pinned, isLastPinned(index, pinnedIndexes)),
    style: pinned ? { left: offsets[index] ?? 0 } : undefined,
  }
}

export function pinnedCellProps({ index, rowIndex, pinnedIndexes, offsets }) {
  if (!pinnedIndexes.includes(index)) {
    return {}
  }

  return {
    className: cn('sticky z-20', rowIndex % 2 ? 'twy-bg-base' : 'twy-bg-base-accent', isLastPinned(index, pinnedIndexes) && PINNED_DIVIDER_CLASS),
    style: { left: offsets[index] ?? 0 },
  }
}
